

import { useState } from 'react';
import { Stack, Button, Text, Divider, Collapse, Box, Group, Tooltip } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { NODE_CATEGORIES } from '@/features/editor/nodes';

interface Props {
  onAdd: (type: string) => void;
}

export default function NodePalette({ onAdd }: Props) {
  // Track which categories are expanded (all expanded by default)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(NODE_CATEGORIES.map((cat) => cat.id))
  );

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  return (
    <Stack gap="xs">
      <Text fw={600} size="sm">
        Node Palette
      </Text>
      <Divider />

      {NODE_CATEGORIES.map((category) => {
        const isExpanded = expandedCategories.has(category.id);

        return (
          <Box key={category.id}>
            {/* Category Header */}
            <Button
              variant="subtle"
              size="xs"
              fullWidth
              onClick={() => toggleCategory(category.id)}
              styles={{
                root: {
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  paddingLeft: '4px',
                },
              }}
              leftSection={isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            >
              <Group gap={4}>
                {category.icon && category.icon}
                <Text size="xs" fw={600}>{category.label}</Text>
              </Group>
            </Button>

            {/* Category Nodes */}
            <Collapse in={isExpanded}>
              <Stack gap={4} pl="md" mt={4}>
                {category.nodes.map((node) => (
                  <Tooltip
                    key={node.type}
                    label={node.description}
                    position="right"
                    withArrow
                    disabled={!node.description}
                  >
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => onAdd(node.type)}
                      fullWidth
                      styles={{
                        root: {
                          justifyContent: 'flex-start',
                          height: '28px',
                        },
                      }}
                    >
                      <Group gap={6}>
                        <Box
                          w={8}
                          h={8}
                          style={{
                            borderRadius: '50%',
                            backgroundColor: node.color,
                          }}
                        />
                        <Text size="xs">{node.label}</Text>
                      </Group>
                    </Button>
                  </Tooltip>
                ))}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}
